import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { Dispatch } from 'redux';

import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { ListTypeEnum } from 'APIPackage/CatalogsModule/enums/ListTypeEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmListTableColumnsModal } from 'KnmListPackage/MainModule/components/KnmListTableColumnsModal';
import { planInitPage } from 'PlanPackage/MainModule/actions/planActions';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { getSortOrderByPeriod } from 'CorePackage/MainModule/common/getSortOrderByPeriod';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { KnmListTable } from 'KnmListPackage/MainModule/components/KnmListTable';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';
import { getUserRoleIsProsecutor } from 'AuthPackage/MainModule/selectors/authSelectors';

import {
  knmListByPlanInitPage,
  knmListByPlanResetPage,
  knmListByPlanSetBasicFilters,
  knmListByPlanGetData,
  knmListByPlanGetDataNextPage,
  knmListByPlanSetOrder,
  knmListByPlanDelete,
} from '../actions/knmListByPlanActions';
import { getKnmListByPlanData, getKnmListByPlanTotalCount } from '../selectors/knmListByPlanSelectors';
import { initialKnmListByPlanTableColumns } from '../constants/initialKnmListByPlanTableColumns';
import { KnmListByPlanHeader } from './KnmListByPlanHeader';
import { KnmListByPlanPanel } from './KnmListByPlanPanel';
import { adminGetFetchSettings } from '../../../PersonalAccountPackage/AdminModule/actions/adminAction';

interface IKnmListByPlanProps {
  knms: IKnmRaw[];
  knmsTotalCount: number;
  isProsecutor: boolean | undefined;
}

const KnmListByPlanWithoutStore = ({ knms, knmsTotalCount, isProsecutor }: IKnmListByPlanProps) => {
  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>({});
  const [tableColumns, setTableColumns] = useState<ITableColumn<IKnmRaw>[]>(
    initialKnmListByPlanTableColumns(isProsecutor ?? false),
  );
  const [isKnmListTableColumnsModalVisible, setIsKnmListTableColumnsModalVisible] = useState(false);
  const { id: pathPlanId, type: pathTypeList } = useParams<{ id: string; type: ListTypeEnum }>();
  const planId = Number(pathPlanId);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(knmListByPlanInitPage());

    return () => {
      dispatch(knmListByPlanResetPage());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(planInitPage(pathPlanId));
  }, [dispatch, pathPlanId]);

  useEffect(() => {
    dispatch(knmListByPlanSetBasicFilters(planId, pathTypeList.toUpperCase() as ListTypeEnum));
  }, [dispatch, planId, pathTypeList]);

  useEffect(() => {
    const order = getSortOrder(initialKnmListByPlanTableColumns(isProsecutor ?? false), getSortOrderByPeriod);

    dispatch(knmListByPlanSetOrder(order));
  }, [dispatch]);

  useEffect(() => {
    const newItemsCheck: TableCheckItems = {};

    knms.forEach(item => {
      newItemsCheck[item.id] = itemsCheck[item.id];
    });
    setItemsCheck(newItemsCheck);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knms]);

  useEffect(() => {
    setTableColumns(initialKnmListByPlanTableColumns(isProsecutor ?? false));
  }, [isProsecutor]);

  const onChangeSort = useCallback(
    (newColumnsData: ITableColumn<IKnmRaw>[]) => {
      const newOrder = getSortOrder(newColumnsData, getSortOrderByPeriod);

      dispatch(knmListByPlanSetOrder(newOrder));
      dispatch(knmListByPlanGetData());
    },
    [dispatch],
  );

  const openKnmListTableColumnsModal = useCallback(() => setIsKnmListTableColumnsModalVisible(true), []);
  const closeKnmListTableColumnsModal = useCallback(() => setIsKnmListTableColumnsModalVisible(false), []);

  const handleLoadClick = useCallback(() => dispatch(knmListByPlanGetDataNextPage()), [dispatch]);

  const handleDeleteClick = useCallback(() => {
    setIsConfirmModalVisible(false);
    const ids = Object.entries(itemsCheck)
      .filter(item => item[1])
      .map(item => Number(item[0]));

    dispatch(knmListByPlanDelete(ids));
  }, [dispatch, itemsCheck]);

  const handleDeleteModalClose = useCallback(() => setIsConfirmModalVisible(false), []);

  const handleDropdownDeleteButtonClick = useCallback(() => {
    setIsConfirmModalVisible(true);
  }, []);

  useEffect(() => {
    dispatch(adminGetFetchSettings());
  }, [dispatch]);

  return (
    <TablePage>
      <KnmListByPlanHeader />
      <KnmListByPlanPanel
        itemsCheck={itemsCheck}
        planIdNumber={planId}
        handleDropdownDeleteButtonClick={handleDropdownDeleteButtonClick}
      />
      <KnmListTable
        tableColumns={tableColumns}
        itemsCheck={itemsCheck}
        knms={knms}
        totalCount={knmsTotalCount}
        isConfirmModalVisible={isConfirmModalVisible}
        setItemsCheck={setItemsCheck}
        setTableColumns={setTableColumns}
        handleLoadClick={handleLoadClick}
        onChangeSort={onChangeSort}
        handleDeleteClick={handleDeleteClick}
        handleDeleteModalClose={handleDeleteModalClose}
        handleSettingsButtonClick={openKnmListTableColumnsModal}
      />
      <KnmListTableColumnsModal
        visible={isKnmListTableColumnsModalVisible}
        tableItems={tableColumns}
        onApply={setTableColumns}
        onClose={closeKnmListTableColumnsModal}
      />
    </TablePage>
  );
};

const mapStateToProps = (state: IRootState) => ({
  knms: getKnmListByPlanData(state),
  knmsTotalCount: getKnmListByPlanTotalCount(state),
  isProsecutor: getUserRoleIsProsecutor(state),
});

export const KnmListByPlan = connect(mapStateToProps)(KnmListByPlanWithoutStore);
